import { useState } from "react"
import { XIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface Row {
  key: string
  value: string
}

function stringify(value: any): string {
  if (value === null) return "null"
  if (typeof value === "string") return value
  return String(value)
}

function parseValue(text: string): any {
  const trimmed = text.trim()
  if (trimmed === "true") return true
  if (trimmed === "false") return false
  if (trimmed === "null") return null
  if (trimmed !== "" && !Number.isNaN(Number(trimmed))) return Number(trimmed)
  return text
}

function toRows(set: { [key: string]: any }): Row[] {
  return Object.entries(set).map(([key, value]) => ({ key, value: stringify(value) }))
}

function toSet(rows: Row[], counter: boolean): { [key: string]: any } {
  const set: { [key: string]: any } = {}
  for (const row of rows) if (row.key) set[row.key] = counter ? Number(row.value) || 0 : parseValue(row.value)
  return set
}

interface Props {
  set: { [key: string]: any }
  onChange: (set: { [key: string]: any }) => void
  counter?: boolean
}

export function SetVarsSection({ set, onChange, counter = false }: Props) {
  const [rows, setRows] = useState<Row[]>(toRows(set))

  function commit(next: Row[]) {
    setRows(next)
    onChange(toSet(next, counter))
  }

  function updateRow(index: number, patch: Partial<Row>) {
    commit(rows.map((row, i) => i === index ? { ...row, ...patch } : row))
  }

  function addRow() {
    commit([...rows, { key: "", value: "" }])
  }

  function removeRow(index: number) {
    commit(rows.filter((_, i) => i !== index))
  }

  return <div className="flex flex-col gap-2">
    <h3 className="text-sm font-medium">{counter ? "Add to counters" : "Set variables"}</h3>
    {counter && <p className="text-xs text-muted-foreground">Added after the set above. An unset var counts as 0, a negative amount subtracts</p>}
    {rows.map((row, index) => <div key={index} className="flex items-center gap-2">
      <Input value={row.key} onChange={e => updateRow(index, { key: e.target.value })} placeholder="var name" className="flex-1" />
      <Input value={row.value} onChange={e => updateRow(index, { value: e.target.value })} placeholder={counter ? "amount" : "value"} className="flex-1" />
      <Button variant="ghost" size="icon-xs" aria-label="Remove variable" onClick={() => removeRow(index)}><XIcon /></Button>
    </div>)}
    <Button variant="outline" size="sm" onClick={addRow} className="self-start">{counter ? "Add counter" : "Add variable"}</Button>
  </div>
}
